import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';

import { Segmented } from '@/components/Segmented';
import { DailyBreakdownReport } from '@/features/time/DailyBreakdownReport';
import { EstimateVsActualTable } from '@/features/time/EstimateVsActualTable';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  REPORTS_TABS,
  type ReportsTab,
  resolveReportsTab,
} from '@/lib/views';

interface TimeRow {
  client_id: number;
  client_name: string;
  project_name: string | null;
  logged_seconds: number;
  billable_seconds: number;
  amount: string;
  currency: string | null;
}

interface BurnDown {
  client_id: number;
  client_name: string;
  budget_type: string;
  budget_amount: string;
  used: string;
  remaining: string;
  over_budget: boolean;
}

interface Calibration {
  project_name: string | null;
  sample_size: number;
  median_estimate_minutes: number;
  median_actual_minutes: number;
  ratio: number;
}

const hours = (seconds: number) => (seconds / 3600).toFixed(2);

function syncReportsTab(tab: ReportsTab) {
  const params = new URLSearchParams(window.location.search);
  if (tab === 'overview') params.delete('tab');
  else params.set('tab', tab);
  const qs = params.toString();
  const next = qs ? `/?${qs}` : '/';
  if (`${window.location.pathname}${window.location.search}` !== next) {
    window.history.replaceState(null, '', next);
  }
}

export function ReportsPage() {
  const [tab, setTab] = useState<ReportsTab>(() =>
    resolveReportsTab(window.location.search),
  );

  useEffect(() => {
    syncReportsTab(tab);
  }, [tab]);

  const time = useQuery<TimeRow[]>({
    queryKey: ['reports', 'time'],
    queryFn: () => api<TimeRow[]>('/api/reports/time'),
    enabled: tab === 'overview',
  });
  const burndown = useQuery<BurnDown[]>({
    queryKey: ['reports', 'burndown'],
    queryFn: () => api<BurnDown[]>('/api/reports/burndown'),
    enabled: tab === 'overview',
  });
  const calibration = useQuery<Calibration[]>({
    queryKey: ['reports', 'calibration'],
    queryFn: () => api<Calibration[]>('/api/reports/calibration'),
    enabled: tab === 'overview',
  });

  const totalSeconds = (time.data ?? []).reduce((s, r) => s + r.logged_seconds, 0);

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-6">
      <header className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-title font-semibold">Reports</h1>
          {tab === 'overview' && (
            <p className="mt-1 text-small text-text-secondary">
              Last 30 days · <span className="tabular">{hours(totalSeconds)}h</span> logged
            </p>
          )}
          {tab === 'daily' && (
            <p className="mt-1 text-small text-text-secondary">
              Day journal and breakdown
            </p>
          )}
        </div>

        {tab === 'overview' && (
          /* A real link, not a scripted download: it survives right-click,
              middle-click, and the browser's own download handling. */
          <a
            href="/api/reports/time.csv"
            download
            className={cn(
              'pressable inline-flex min-h-touch items-center gap-2 rounded px-3',
              'bg-surface-2 text-small font-medium hover:bg-surface-3',
            )}
          >
            <Download size={14} strokeWidth={1.5} aria-hidden />
            CSV
          </a>
        )}
      </header>

      <Segmented
        label="View"
        value={tab}
        options={REPORTS_TABS}
        onChange={setTab}
        className="mb-6"
      />

      {tab === 'overview' && (
        <>
          <Section title="Time by project">
            {time.isError && !time.data && (
              <p role="alert" className="text-body text-danger">
                Could not load time report.{' '}
                <button type="button" className="underline" onClick={() => time.refetch()}>
                  Retry
                </button>
              </p>
            )}
            {time.data?.length === 0 && <Empty>No time logged in this period.</Empty>}
            {time.data && time.data.length > 0 && (
              <>
                <ul className="flex flex-col gap-2 sm:hidden">
                  {time.data.map((row) => (
                    <li
                      key={`${row.client_id}-${row.project_name}`}
                      className="rounded-lg border border-border bg-surface-1 px-3 py-2.5"
                    >
                      <p className="truncate text-small font-medium">{row.client_name}</p>
                      <p className="truncate text-label text-text-secondary">
                        {row.project_name ?? '-'}
                      </p>
                      <dl className="mt-2 grid grid-cols-3 gap-2 text-label">
                        <div>
                          <dt className="text-text-tertiary">Hours</dt>
                          <dd className="tabular">{hours(row.logged_seconds)}</dd>
                        </div>
                        <div>
                          <dt className="text-text-tertiary">Billable</dt>
                          <dd className="tabular">{hours(row.billable_seconds)}</dd>
                        </div>
                        <div>
                          <dt className="text-text-tertiary">Amount</dt>
                          <dd className="tabular">
                            {Number(row.amount) > 0
                              ? `${row.currency ?? ''} ${row.amount}`
                              : '-'}
                          </dd>
                        </div>
                      </dl>
                    </li>
                  ))}
                </ul>
                <div className="hidden overflow-x-auto sm:block">
                  <table className="w-full text-small">
                    <thead>
                      <tr className="border-b border-border text-left text-label uppercase text-text-tertiary">
                        <th className="py-2 font-medium">Client</th>
                        <th className="py-2 font-medium">Project</th>
                        <th className="py-2 text-right font-medium">Hours</th>
                        <th className="py-2 text-right font-medium">Billable</th>
                        <th className="py-2 text-right font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {time.data.map((row) => (
                        <tr
                          key={`${row.client_id}-${row.project_name}`}
                          className="border-b border-border last:border-0"
                        >
                          <td className="py-2">{row.client_name}</td>
                          <td className="py-2 text-text-secondary">{row.project_name ?? '-'}</td>
                          <td className="tabular py-2 text-right">{hours(row.logged_seconds)}</td>
                          <td className="tabular py-2 text-right">{hours(row.billable_seconds)}</td>
                          <td className="tabular py-2 text-right">
                            {Number(row.amount) > 0 ? `${row.currency ?? ''} ${row.amount}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Section>

          <Section title="Budgets">
            {burndown.isError && !burndown.data && (
              <p role="alert" className="text-body text-danger">
                Could not load budgets.{' '}
                <button type="button" className="underline" onClick={() => burndown.refetch()}>
                  Retry
                </button>
              </p>
            )}
            {burndown.data?.length === 0 && <Empty>No client has a budget set.</Empty>}
            <div className="flex flex-col gap-3">
              {burndown.data?.map((row) => {
                const used = Number(row.used);
                const total = Number(row.budget_amount);
                const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;

                return (
                  <div key={row.client_id}>
                    <div className="flex items-baseline justify-between gap-3 text-small">
                      <span className="font-medium">{row.client_name}</span>
                      <span className={cn('tabular', row.over_budget && 'text-danger')}>
                        {row.used} / {row.budget_amount}
                        {row.budget_type === 'retainer_hours' ? 'h' : ''}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-3">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          row.over_budget ? 'bg-danger' : 'bg-accent',
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {/* Over budget is stated in words as well as color -- color
                        alone is not a message. */}
                    {row.over_budget && (
                      <p className="mt-1 text-label text-danger">
                        Over by {Math.abs(Number(row.remaining))}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Estimate accuracy">
            {calibration.isError && !calibration.data && (
              <p role="alert" className="text-body text-danger">
                Could not load estimate accuracy.{' '}
                <button type="button" className="underline" onClick={() => calibration.refetch()}>
                  Retry
                </button>
              </p>
            )}
            {calibration.data?.length === 0 && (
              <Empty>
                Not enough finished work yet. This needs at least three completed tasks with
                both an estimate and logged time.
              </Empty>
            )}
            <div className="flex flex-col gap-2">
              {calibration.data?.map((row) => (
                <p key={row.project_name ?? 'none'} className="text-small">
                  <span className="font-medium">{row.project_name ?? 'No project'}</span>{' '}
                  <span className="text-text-secondary">
                    {row.ratio > 1
                      ? `takes ${row.ratio}× longer than you estimate`
                      : `runs ${(1 / row.ratio).toFixed(2)}× faster than you estimate`}{' '}
                    <span className="tabular">(n={row.sample_size})</span>
                  </span>
                </p>
              ))}
            </div>
          </Section>

          <EstimateVsActualTable />
        </>
      )}

      {tab === 'daily' && <DailyBreakdownReport />}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-3 text-label font-medium uppercase text-text-tertiary">{title}</h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-small text-text-tertiary">{children}</p>;
}
